import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { dial, setMyState } from '../lib/actions';
import { REASON_LABEL, STATE_COLOR, STATE_LABEL, useNova, View } from '../lib/store';
import type { AgentState } from '../lib/types';
import { ComposeEmail } from './ComposeEmail';
import { StartWhatsApp } from './WaTemplates';
import { Mark } from './Login';
import { ThemePicker } from './ThemePicker';
import { Drawer, Field, inputClass } from './admin/kit';
import { act } from '../lib/actions';
import { api } from '../lib/api';
import { Button, duration, Elapsed, Icon, StateDot } from './ui';

const VIEW_TITLE: Record<View, string> = {
  home: 'Home',
  cockpit: 'Work',
  ops: 'Live operations',
  history: 'History',
  recordings: 'Recordings',
  quality: 'Quality',
  reports: 'Reports',
  admin: 'Admin',
};

/** Closes a popover on outside click or Escape. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
}

export function TopBar({ onSignOut }: { onSignOut: () => void }) {
  const view = useNova((s) => s.view);
  const org = useNova((s) => s.org);
  const link = useNova((s) => s.link);
  const phone = useNova((s) => s.phone);
  // Users without an extension never take calls; no phone light for them
  const noExtension = useNova((s) => s.phone === 'off' && Boolean(s.phoneError?.startsWith('No extension')));

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-hull px-3 sm:gap-3">
      <div className="flex w-10 justify-center" title={org?.name ?? 'Omni'}>
        <Mark letter="O" size={30} radius={7} fontSize={15} />
      </div>
      <p className="sr-only">
        {VIEW_TITLE[view]}, {org?.name}
      </p>

      <div className="ml-auto flex items-center gap-1">
        <span className="hidden sm:block">
          <IconButton label="Search or run a command (Ctrl K)" onClick={() => useNova.getState().set({ paletteOpen: true })}>
            <Icon name="search" size={18} />
          </IconButton>
        </span>
        <NewCall />
        {noExtension ? null : (
          <ConnectionLight
            ok={phone === 'registered'}
            warn={phone === 'connecting'}
            icon="phone"
            title={phone === 'registered' ? 'Softphone ready' : phone === 'connecting' ? 'Softphone connecting' : 'Softphone not connected'}
          />
        )}
        <ConnectionLight
          ok={link === 'live'}
          warn={link !== 'live'}
          icon="signal"
          title={link === 'live' ? 'Live updates connected' : 'Reconnecting to live updates'}
        />
        <div className="ml-2">
          <StatePicker onSignOut={onSignOut} />
        </div>
      </div>
    </header>
  );
}

/** Orange bar under the top bar while someone who takes calls is not taking them (as on the Sync AI Omni portal). */
export function PausedBanner() {
  const me = useNova((s) => s.me);
  const takesCalls = useNova((s) => Boolean(s.user && s.people[s.user.sub]?.extension));
  const breaks = useNova((s) => s.breakReasons);
  if (!takesCalls || !me || !['away', 'break', 'offline'].includes(me.state)) return null;
  const why =
    me.state === 'break'
      ? (breaks.find((b) => b.code === me.reason)?.label ?? REASON_LABEL[me.reason ?? ''] ?? 'Break')
      : me.state === 'offline'
        ? 'Offline'
        : 'Away';
  return (
    <div
      role="status"
      className="flex h-[30px] shrink-0 items-center gap-3 px-3 text-[13px] font-bold text-white"
      style={{ background: me.state === 'offline' ? 'var(--color-offline)' : '#f0901f' }}
    >
      <span>You are Paused - {why}</span>
      <Elapsed since={me.since} className="font-normal opacity-90" />
      <button type="button" onClick={() => void setMyState('available')} className="ml-auto rounded px-2 py-0.5 text-[12px] font-semibold hover:bg-white/20">
        Go available
      </button>
    </div>
  );
}

function IconButton({ label, onClick, children, active = false }: { label: string; onClick?: () => void; children: ReactNode; active?: boolean }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`grid h-9 w-9 place-items-center rounded-lg transition ${active ? 'bg-veil/[0.08] text-ink' : 'text-ink-2 hover:bg-veil/[0.06] hover:text-ink'}`}
    >
      {children}
    </button>
  );
}

function ConnectionLight({ ok, warn, icon, title }: { ok: boolean; warn: boolean; icon: 'phone' | 'signal'; title: string }) {
  const color = ok ? 'var(--color-available)' : warn ? 'var(--color-wrapup)' : 'var(--color-break)';
  return (
    <span className="relative grid h-9 w-9 place-items-center text-ink-2" title={title}>
      <Icon name={icon} size={17} />
      <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full ring-2 ring-hull" style={{ background: color }} aria-hidden />
      <span className="sr-only">{title}</span>
    </span>
  );
}

/** Why a colleague cannot be called right now, or null when they can (Break and Away still ring). */
function unreachable(state: AgentState): string | null {
  if (state === 'offline') return 'Signed out';
  if (state === 'on_call' || state === 'reserved') return 'On a call';
  if (state === 'wrap_up') return 'Finishing a call';
  return null;
}

/** "New": call a colleague from the directory, or any number, or write an email. */
function NewCall() {
  const { open, setOpen, ref } = usePopover();
  const [composing, setComposing] = useState(false);
  const [whatsapp, setWhatsapp] = useState(false);
  const phone = useNova((s) => s.phone);
  const people = useNova((s) => s.people);
  const agents = useNova((s) => s.agents);
  const me = useNova((s) => s.user?.sub);
  const [query, setQuery] = useState('');
  const [signedInOnly, setSignedInOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => input.current?.focus(), 0);
  }, [open]);

  const q = query.trim().toLowerCase();
  const digits = query.replace(/\D/g, '');
  const isNumber = /^[+\d\s]+$/.test(query.trim()) && digits.length >= 3;
  // Signed-in people first, then by name; live state from the event stream when there is one
  const list = Object.values(people)
    .filter((p): p is typeof p & { extension: string } => p.id !== me && Boolean(p.extension))
    .map((p) => ({ ...p, state: (agents[p.id] ?? p.live)?.state ?? ('offline' as AgentState) }))
    .filter((p) => !signedInOnly || p.state !== 'offline')
    .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.team ?? '').toLowerCase().includes(q) || (digits !== '' && p.extension.startsWith(digits)))
    .sort((a, b) => Number(a.state === 'offline') - Number(b.state === 'offline') || a.name.localeCompare(b.name));
  const firstCallable = list.find((p) => !unreachable(p.state));
  const canCall = phone === 'registered' && !busy;

  async function call(target: string) {
    setBusy(true);
    const ok = await dial(target);
    setBusy(false);
    if (ok) {
      setQuery('');
      setOpen(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    // A number calls that number (or the extension it is); a name calls the first person it matches
    if (isNumber) void call(query);
    else if (q && firstCallable) void call(firstCallable.extension);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="mx-1 flex h-9 items-center gap-1.5 rounded-md bg-ion px-3.5 text-sm font-semibold text-on-accent hover:brightness-110"
      >
        New <Icon name="chevronDown" size={14} />
      </button>
      {open ? (
        <form
          onSubmit={submit}
          className="absolute right-0 top-11 z-50 w-[min(360px,calc(100vw-24px))] rounded-xl border border-line-strong bg-hull p-4 shadow-2xl shadow-black/60"
        >
          <label htmlFor="new-call" className="mb-2 block text-sm font-medium">
            Call a colleague or number
          </label>
          <div className="flex gap-2">
            <input
              ref={input}
              id="new-call"
              autoComplete="off"
              placeholder="Name, extension or number"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="min-w-0 flex-1 rounded-lg border border-line bg-abyss/60 px-3 py-2 text-sm outline-none focus:border-ion"
            />
            <Button tone="primary" type="submit" className="px-3 py-2" disabled={!canCall || !(isNumber || (q && firstCallable))}>
              <Icon name="phone" size={15} /> Call
            </Button>
          </div>
          <label className="mt-3 flex items-center gap-2 text-xs text-ink-2">
            <input type="checkbox" checked={signedInOnly} onChange={(e) => setSignedInOnly(e.target.checked)} className="accent-[var(--color-ion)]" />
            Signed in only
          </label>
          <ul className="-mx-2 mt-2 max-h-[min(320px,50vh)] overflow-auto" aria-label="People">
            {list.length === 0 ? (
              <li className="px-2 py-3 text-sm text-ink-3">{isNumber ? 'Nobody has that extension; Call dials the number.' : 'Nobody matches.'}</li>
            ) : (
              list.map((p) => {
                const why = unreachable(p.state);
                return (
                  <li key={p.id} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-veil/[0.04]">
                    <StateDot state={p.state} size={8} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{p.name}</span>
                      <span className="block truncate text-xs text-ink-3">
                        <span className="tabular">Ext. {p.extension}</span>
                        {p.team ? ` · ${p.team}` : ''} · {why ?? STATE_LABEL[p.state]}
                      </span>
                    </span>
                    <Button
                      tone="quiet"
                      className="px-2.5 py-1 text-xs"
                      disabled={!canCall || Boolean(why)}
                      onClick={() => void call(p.extension)}
                      aria-label={`Call ${p.name}`}
                      title={why ?? `Call ${p.name} on extension ${p.extension}`}
                    >
                      <Icon name="phone" size={13} /> Call
                    </Button>
                  </li>
                );
              })
            )}
          </ul>
          <p className="mt-2 text-xs text-ink-3">
            {phone === 'registered' ? 'Your softphone rings first, then the person or number is called.' : 'Your softphone must be connected to call.'}
          </p>
          <div className="mt-3 border-t border-line pt-3">
            <Button
              tone="quiet"
              className="w-full justify-center px-3 py-2 text-sm"
              onClick={() => {
                setOpen(false);
                setComposing(true);
              }}
            >
              <Icon name="mail" size={15} /> New email
            </Button>
            <Button
              tone="quiet"
              className="mt-2 w-full justify-center px-3 py-2 text-sm"
              onClick={() => {
                setOpen(false);
                setWhatsapp(true);
              }}
            >
              <Icon name="chat" size={15} /> New WhatsApp message
            </Button>
          </div>
        </form>
      ) : null}
      {composing ? <ComposeEmail onClose={() => setComposing(false)} /> : null}
      {whatsapp ? <StartWhatsApp onClose={() => setWhatsapp(false)} /> : null}
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = {
  agent: 'Agent',
  supervisor: 'Supervisor',
  qa: 'QA',
  manager: 'Manager',
  admin: 'Administrator',
  platform_admin: 'Provider',
};

/**
 * Top right: who you are and your status. Opens a card, as on the Sync AI
 * Omni portal: Available | Away (with the break reasons) | Offline in one
 * row, then your extension and today's numbers, Change password and Logout.
 */
function StatePicker({ onSignOut }: { onSignOut: () => void }) {
  const { open, setOpen, ref } = usePopover();
  const [awayOpen, setAwayOpen] = useState(false);
  const me = useNova((s) => s.me);
  const user = useNova((s) => s.user);
  const stats = useNova((s) => s.myStats);
  const breaks = useNova((s) => s.breakReasons);
  const extension = useNova((s) => (s.user ? s.people[s.user.sub]?.extension : null));
  const [changing, setChanging] = useState(false);
  const state = me?.state ?? 'offline';
  const locked = state === 'on_call' || state === 'reserved';
  const pick = (s: AgentState, reason?: string) => {
    setOpen(false);
    setAwayOpen(false);
    void setMyState(s, reason);
  };
  const paused = state === 'away' || state === 'break';
  const pausedLabel = state === 'break' ? (breaks.find((b) => b.code === me?.reason)?.label ?? 'Break') : 'Away';
  const segment = (active: boolean, color: string) =>
    `flex h-9 flex-1 items-center justify-center gap-1 rounded-md text-[13px] font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
      active ? 'text-white' : 'bg-abyss text-ink hover:brightness-95'
    }` + (active ? ` ${color}` : '');

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={`Your status: ${STATE_LABEL[state]}. Change status`}
        className="flex h-11 items-center gap-2 rounded-lg border border-line bg-hull py-1 pl-1 pr-2.5 hover:border-line-strong"
      >
        <span className="grid h-9 w-9 place-items-center rounded-md bg-ion text-sm font-bold text-on-accent">{initials(user?.name)}</span>
        <span className="hidden text-left leading-tight sm:block">
          <span className="flex items-center gap-1.5 text-[13px] font-semibold">
            <span className="h-2 w-2 rounded-full" style={{ background: STATE_COLOR[state] }} aria-hidden />
            {paused ? pausedLabel : STATE_LABEL[state]}
          </span>
          <Elapsed since={me?.since} className="block text-[11px] text-ink-3" />
        </span>
      </button>

      {changing ? <ChangePassword onDone={() => setChanging(false)} /> : null}
      {open ? (
        <div className="absolute right-0 top-[52px] z-50 w-[260px] rounded-xl border border-line bg-hull p-4 shadow-xl shadow-black/10" role="dialog" aria-label="Your status">
          <div className="flex flex-col items-center text-center">
            <span className="grid h-12 w-12 place-items-center rounded-lg bg-ion text-lg font-bold text-on-accent">{initials(user?.name)}</span>
            <p className="mt-2 font-semibold">{user?.name}</p>
            <p className="text-[12px] text-ink-3">{ROLE_LABEL[user?.role ?? ''] ?? user?.role}</p>
          </div>

          {locked ? <p className="mt-3 text-center text-[12px] text-ink-3">You can change status when the call ends.</p> : null}
          <div className="relative mt-3 flex gap-1.5" role="group" aria-label="Status">
            <button type="button" className={segment(state === 'available', 'bg-available')} disabled={locked} aria-pressed={state === 'available'} onClick={() => pick('available')}>
              Available
            </button>
            <button
              type="button"
              className={segment(paused, 'bg-away')}
              disabled={locked}
              aria-pressed={paused}
              aria-expanded={awayOpen}
              onClick={() => setAwayOpen(!awayOpen)}
            >
              {paused ? pausedLabel : 'Away'} <Icon name="chevronDown" size={12} />
            </button>
            <button type="button" className={segment(state === 'offline', 'bg-offline')} disabled={locked} aria-pressed={state === 'offline'} onClick={() => pick('offline')}>
              Offline
            </button>
            {awayOpen ? (
              <div className="absolute left-1/3 right-0 top-10 z-10 rounded-lg border border-line bg-hull py-1 shadow-lg shadow-black/10" role="menu" aria-label="Away and breaks">
                <MenuItem state="away" label="Away" active={state === 'away'} disabled={locked} onClick={() => pick('away')} />
                {breaks.map((b) => (
                  <MenuItem key={b.id} state="break" label={b.label} active={state === 'break' && me?.reason === b.code} disabled={locked} onClick={() => pick('break', b.code)} />
                ))}
              </div>
            ) : null}
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-3 text-[13px]">
            {extension ? (
              <div className="col-span-2">
                <dt className="text-[11px] text-ink-3">Extension</dt>
                <dd className="tabular">{extension}</dd>
              </div>
            ) : null}
            <div>
              <dt className="text-[11px] text-ink-3">Handled today</dt>
              <dd className="tabular">{stats.handled}</dd>
            </div>
            <div>
              <dt className="text-[11px] text-ink-3">Avg talk</dt>
              <dd className="tabular">{duration(stats.avg_talk)}</dd>
            </div>
          </dl>
          <div className="mt-4">
            <p className="mb-1.5 text-[11px] text-ink-3">Appearance</p>
            <ThemePicker />
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-line pt-3 text-[13px] font-medium text-ion">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setChanging(true);
              }}
              className="hover:underline"
            >
              Change password
            </button>
            <button type="button" onClick={onSignOut} className="hover:underline">
              Logout
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Changes your own password; the current one is asked for first. */
function ChangePassword({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ current: '', password: '', again: '' });
  const [busy, setBusy] = useState(false);
  const mismatch = f.again !== '' && f.again !== f.password;
  async function save() {
    setBusy(true);
    const ok = await act(() => api('POST', '/me/password', { current: f.current, password: f.password }).then(() => true), 'Password changed');
    setBusy(false);
    if (ok) onDone();
  }
  const field = (k: keyof typeof f, label: string, hint?: string) => (
    <Field label={label} hint={hint}>
      {(id) => <input id={id} type="password" autoComplete={k === 'current' ? 'current-password' : 'new-password'} className={inputClass} value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} />}
    </Field>
  );
  return (
    <Drawer
      title="Change password"
      open
      onClose={onDone}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || !f.current || f.password.length < 10 || f.password !== f.again} onClick={() => void save()}>
            Change password
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
        </>
      }
    >
      {field('current', 'Current password')}
      {field('password', 'New password', 'At least 10 characters')}
      {field('again', 'New password again')}
      {mismatch ? <p className="-mt-2 text-xs text-break">The two new passwords are different.</p> : null}
    </Drawer>
  );
}

function MenuItem({ state, label, active, disabled, onClick }: { state: AgentState; label: string; active: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition disabled:opacity-40 ${
        active ? 'bg-veil/[0.07] text-ink' : 'text-ink-2 hover:bg-veil/[0.05] hover:text-ink'
      }`}
    >
      <StateDot state={state} size={8} />
      {label}
    </button>
  );
}

function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** Left icon rail. Becomes a bottom bar on phones. */
export function Rail() {
  const view = useNova((s) => s.view);
  const role = useNova((s) => s.user?.role);
  const waiting = useNova((s) => s.queues.reduce((n, q) => n + q.waiting, 0));
  const items: { id: View; label: string; icon: 'inbox' | 'grid' | 'history' | 'rec' | 'chart' | 'settings' }[] = [
    // The dashboard comes first, then Work: Home for agents, Live operations for supervisors and admins
    ...(role === 'agent' ? [{ id: 'home' as View, label: 'Home', icon: 'grid' as const }] : []),
    ...(role !== 'agent' && role !== 'qa' ? [{ id: 'ops' as View, label: 'Live operations', icon: 'grid' as const }] : []),
    { id: 'cockpit', label: 'Work', icon: 'inbox' },
    // One tab for reports, the call log and recordings
    { id: 'reports', label: 'Reports', icon: 'chart' },
    ...(role === 'admin' || role === 'platform_admin' ? [{ id: 'admin' as View, label: 'Admin', icon: 'settings' as const }] : []),
  ];
  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-40 flex h-14 items-center justify-around border-t border-line bg-hull/95 backdrop-blur-xl md:static md:h-auto md:w-14 md:flex-col md:justify-start md:gap-1 md:border-r md:border-t-0 md:bg-hull/60 md:py-3"
    >
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          title={it.label}
          aria-label={it.label}
          aria-current={view === it.id || (it.id === 'reports' && (view === 'history' || view === 'recordings' || view === 'quality')) ? 'page' : undefined}
          onClick={() => useNova.getState().set({ view: it.id })}
          className={`relative grid h-10 w-10 place-items-center rounded-lg transition ${
            view === it.id || (it.id === 'reports' && (view === 'history' || view === 'recordings' || view === 'quality')) ? 'bg-ion text-on-accent' : 'text-ink-3 hover:bg-veil/[0.06] hover:text-ink'
          }`}
        >
          <Icon name={it.icon} size={19} />
          {it.id === 'ops' && waiting > 0 ? (
            <span className="tabular absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-wrapup px-1 text-[10px] font-semibold text-on-accent">
              {waiting}
            </span>
          ) : null}
        </button>
      ))}
    </nav>
  );
}
